/**
 * BelajarKu Learning Engine V2 — LE-V2-05 Correction Mode Contract.
 * Depends on LE-V2-04 staged batch receipts.
 */

import type { StudyResponse, ValidationIssue } from './batchContract';

export const LE_V2_05_CONTRACT_VERSION = 'le-v2-05.1' as const;

export type CorrectionSubmissionItemV2 = {
  questionInstanceId: string;
  correctedResponse: StudyResponse;
  startedAt: string;
  completedAt: string;
  elapsedDurationMs: number;
  activeDurationMs: number;
  hintCount: number;
  ink?: {
    storageKey?: string;
    checksum?: string;
    strokeCount?: number;
    pointCount?: number;
    formatVersion?: string;
  } | null;
};

export type CorrectionSubmissionV2 = {
  schemaVersion: 2;
  contractVersion: typeof LE_V2_05_CONTRACT_VERSION;
  clientCorrectionSubmissionId: string;
  clientBatchSubmissionId: string;
  studySessionId: string;
  assignmentId: string;
  submittedAt: string;
  items: CorrectionSubmissionItemV2[];
};

export type CorrectionMarkupV2 = {
  layer: 'grader';
  items: Array<{
    questionInstanceId: string;
    type: 'incorrect_mark';
    feedbackCode: 'TRY_AGAIN';
  }>;
};

export type CorrectionResultV2 = {
  schemaVersion: 2;
  contractVersion: typeof LE_V2_05_CONTRACT_VERSION;
  clientCorrectionSubmissionId: string;
  clientBatchSubmissionId: string;
  status: 'completed' | 'correction_required' | 'duplicate';
  originalStatus?: 'completed' | 'correction_required';
  roundNumber: number;
  correction: {
    remainingQuestionInstanceIds: string[];
    correctedThisRound: number;
    stillIncorrectThisRound: number;
  };
  markup: CorrectionMarkupV2;
  commit: {
    state: 'finalized' | 'deferred';
    finalizedItemCount: number;
  };
  nextMode: 'study_complete' | 'correction';
};

const UUIDISH = /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i;
const validDate = (x: unknown) => typeof x === 'string' && x.length >= 20 && Number.isFinite(Date.parse(x));
const nnInt = (x: unknown): x is number => typeof x === 'number' && Number.isInteger(x) && x >= 0;

export function validateCorrectionSubmissionV2(input: unknown): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const add = (code: string, path: string, message: string) => issues.push({ code, path, message });
  if (!input || typeof input !== 'object') return [{ code: 'INVALID_CORRECTION', path: '$', message: 'Correction submission must be an object.' }];
  const x = input as Record<string, unknown>;
  if (x.schemaVersion !== 2) add('INVALID_SCHEMA_VERSION', 'schemaVersion', 'schemaVersion must be 2.');
  if (x.contractVersion !== LE_V2_05_CONTRACT_VERSION) add('INVALID_CONTRACT_VERSION', 'contractVersion', `contractVersion must be ${LE_V2_05_CONTRACT_VERSION}.`);
  for (const key of ['clientCorrectionSubmissionId','clientBatchSubmissionId'] as const) {
    const value=x[key];
    if (typeof value !== 'string' || value.trim().length < 8 || value.trim().length > 128) add('INVALID_SUBMISSION_ID',key,`${key} must contain 8–128 characters.`);
  }
  for (const key of ['studySessionId','assignmentId'] as const) {
    if (typeof x[key] !== 'string' || !UUIDISH.test(x[key] as string)) add('INVALID_ID',key,`${key} must be UUID-like.`);
  }
  if (!validDate(x.submittedAt)) add('INVALID_SUBMITTED_AT','submittedAt','submittedAt must be an ISO timestamp.');
  if (!Array.isArray(x.items) || x.items.length === 0) { add('EMPTY_CORRECTION','items','At least one correction item is required.'); return issues; }
  if (x.items.length > 200) add('CORRECTION_TOO_LARGE','items','Correction cannot contain more than 200 items.');
  const ids=new Set<string>();
  x.items.forEach((raw,index)=>{
    const path=`items[${index}]`;
    if(!raw || typeof raw!=='object'){add('INVALID_ITEM',path,'Correction item must be an object.');return;}
    const item=raw as Record<string,unknown>;
    if(typeof item.questionInstanceId!=='string' || !UUIDISH.test(item.questionInstanceId)) add('INVALID_ID',`${path}.questionInstanceId`,'questionInstanceId must be UUID-like.');
    else if(ids.has(item.questionInstanceId)) add('DUPLICATE_QUESTION',`${path}.questionInstanceId`,'Correction question cannot repeat.');
    else ids.add(item.questionInstanceId);
    if(!item.correctedResponse || typeof item.correctedResponse!=='object') add('MISSING_CORRECTED_RESPONSE',`${path}.correctedResponse`,'correctedResponse is required.');
    if('firstResponse' in item || 'expectedAnswer' in item || 'correctAnswer' in item) add('FORBIDDEN_CORRECTION_FIELD',path,'First/correct answer data is server-owned.');
    if(!validDate(item.startedAt)) add('INVALID_STARTED_AT',`${path}.startedAt`,'startedAt must be ISO.');
    if(!validDate(item.completedAt)) add('INVALID_COMPLETED_AT',`${path}.completedAt`,'completedAt must be ISO.');
    for(const key of ['elapsedDurationMs','activeDurationMs','hintCount'] as const) if(!nnInt(item[key])) add('INVALID_ITEM_TELEMETRY',`${path}.${key}`,`${key} must be non-negative integer.`);
    if(nnInt(item.elapsedDurationMs)&&nnInt(item.activeDurationMs)&&item.activeDurationMs>item.elapsedDurationMs) add('ITEM_ACTIVE_EXCEEDS_ELAPSED',`${path}.activeDurationMs`,'Active duration cannot exceed elapsed duration.');
  });
  return issues;
}

export function correctionMarkup(questionInstanceIds: string[]): CorrectionMarkupV2 {
  return {
    layer: 'grader',
    items: questionInstanceIds.map(questionInstanceId=>({questionInstanceId,type:'incorrect_mark',feedbackCode:'TRY_AGAIN'})),
  };
}
